import { MAX_FIRST_COMMENTS } from "@postdaily/api/schemas/composer-limits";
import { planConfigs } from "@postdaily/api/services/plan-limits";
import { xPostsPerMonth } from "@postdaily/api/services/x-credit-rates";

import type { PlatformId } from "@/components/social-accounts/provider-icon";
import { joinWithAnd } from "@/lib/join-with-and";
import type { CrossLink } from "./cross-links";

/**
 * Per-network landing pages — the site's top-of-funnel doors.
 *
 * Every other public page is either about PostDaily (home, features, pricing)
 * or for people who already signed up (the help centre). Nothing answered the
 * query someone types *before* they know we exist: "schedule bluesky posts".
 * These pages are that answer, one network at a time.
 *
 * They share a renderer, which makes it tempting to treat them as a template
 * with the name swapped — that is exactly the thin-doorway pattern Google's
 * scaled-content-abuse policy targets, and it would also be useless to read.
 * So the copy here is network-specific and load-bearing: real limits, the real
 * counting rule each adapter uses, and the real caveats. `caveat` is required
 * for that reason — a page with nothing honest to say about a network's limits
 * has not earned the URL.
 */

export type NetworkFaq = { q: string; a: string };

export type NetworkSection = {
  heading: string;
  /** Paragraphs. Kept as prose — these pages are read, not skimmed. */
  body: string[];
};

export type NetworkLanding = {
  /** Drives the brand mark and the provider icon. */
  platform: PlatformId;
  /** Route path, without a leading slash. */
  slug: string;
  /**
   * The same path as a typed router target. Declared as a literal union rather
   * than built from `slug`, because TanStack Router type-checks `<Link to>`
   * against the generated route tree and a template string is not narrow
   * enough to satisfy it.
   */
  to:
    | "/bluesky-scheduler"
    | "/mastodon-scheduler"
    | "/facebook-scheduler"
    | "/instagram-scheduler"
    | "/tiktok-scheduler"
    | "/pinterest-scheduler"
    | "/youtube-scheduler"
    | "/threads-scheduler"
    | "/twitter-scheduler"
    | "/linkedin-scheduler";
  network: string;
  /** <title>. Leads with the query intent, keeps the site's brand suffix. */
  metaTitle: string;
  metaDescription: string;
  h1: string;
  intro: string;
  /** Short factual chips under the hero. Keep to hard facts, not adjectives. */
  facts: string[];
  sections: NetworkSection[];
  /**
   * The limitation we lead with rather than bury. Trust is the whole product
   * pitch, and a scheduler that hides a publishing constraint until the post
   * fails has already lost the argument.
   */
  caveat: { heading: string; body: string };
  faqs: NetworkFaq[];
  /** Slugs in `help-data.ts`. Internal links from a door to the real docs. */
  helpSlugs: string[];
  /** Blog guides for this network (see `cross-links.ts`). */
  links?: readonly CrossLink[];
  /**
   * The free public tools for this network. A door page should hand a visitor
   * something useful they can run without signing up, and the tool pages are
   * the strongest internal links this site has to give. A list because a
   * network can have more than one — Bluesky has two.
   */
  tools?: ReadonlyArray<{
    to:
      | "/tools/bluesky-best-time"
      | "/tools/bluesky-engagement-rate"
      | "/tools/mastodon-best-time"
      | "/tools/mastodon-limits"
      | "/tools/thread-splitter"
      | "/tools/video-checker";
    label: string;
    blurb: string;
  }>;
};

/*
  Plan caps quoted on the pages, read from the plans data so a change to a plan
  can't leave a landing page promising the old number. Several are lower than
  what the network itself allows, which is exactly why the pages say so.
*/
const PLAN_CODES = ["free", "starter", "pro", "agency"] as const;
const MB = 1024 * 1024;

function perPlan(value: (code: (typeof PLAN_CODES)[number]) => string) {
  return joinWithAnd(
    PLAN_CODES.map((code) => `${value(code)} on ${planConfigs[code].name}`),
  );
}

const MEDIA_PER_POST = perPlan((code) =>
  String(planConfigs[code].limits.maxPostMediaItems),
);
const UPLOAD_CAP = perPlan(
  (code) => `${planConfigs[code].limits.maxMediaFileBytes / MB} MB`,
);
const LARGEST_UPLOAD_MB = planConfigs.agency.limits.maxMediaFileBytes / MB;
const FREE_PLAN = `a free plan with no credit card: ${planConfigs.free.limits.maxSocialAccounts} channel and ${planConfigs.free.limits.maxScheduledPostsPerMonth} scheduled posts a month`;
const X_PAID_PLANS = (["starter", "pro", "agency"] as const).map((code) => ({
  name: planConfigs[code].name,
  ...xPostsPerMonth(planConfigs[code].limits.maxXCreditsPerMonth),
}));
const X_PLAIN = joinWithAnd(
  X_PAID_PLANS.map((plan, index) =>
    index === 0
      ? `about ${plan.plain} posts on ${plan.name}`
      : `${plan.plain} on ${plan.name}`,
  ),
);
const X_LINKS = joinWithAnd(
  X_PAID_PLANS.map((plan) => `${plan.withLink} on ${plan.name}`),
);

export const NETWORK_LANDINGS = {
  "bluesky-scheduler": {
    platform: "bluesky",
    slug: "bluesky-scheduler",
    links: [
      {
        blog: "how-to-schedule-bluesky-posts",
        label: "How to schedule Bluesky posts",
      },
      {
        blog: "bluesky-app-passwords-explained",
        label: "Bluesky app passwords, explained",
      },
      { blog: "bluesky-threads-guide", label: "Writing a Bluesky thread" },
      {
        blog: "how-often-to-post-on-bluesky",
        label: "How often to post on Bluesky",
      },
      {
        blog: "bluesky-analytics-what-you-can-measure",
        label: "What Bluesky analytics can measure",
      },
    ],
    to: "/bluesky-scheduler",
    network: "Bluesky",
    metaTitle: "Bluesky Scheduler: Posts, Threads & Video — PostDaily",
    metaDescription:
      "Schedule Bluesky posts, threads and video from one queue — with grapheme-accurate character counting and publishing that retries instead of failing quietly.",
    h1: "Bluesky scheduler for posts, threads and video",
    intro:
      "Queue posts and threads to Bluesky, publish them at the times your followers are awake, and get told when something breaks instead of discovering it a week later.",
    facts: [
      "300 characters, counted as graphemes",
      "Threads with per-segment media",
      "Images and video",
      "Free plan, no card",
    ],
    sections: [
      {
        heading: "Timing matters more on Bluesky than anywhere else",
        body: [
          "Bluesky's default Following feed is chronological. There is no ranking model deciding to resurface a good post six hours later — if your followers were not looking when you posted, they will very likely never see it at all.",
          "That makes scheduling a different job here than on an algorithmic network. PostDaily fills a posting queue from a weekly time grid you set once, so every post lands in a slot you chose deliberately rather than whenever you happened to open the app. You can set the grid per channel, so your Bluesky rhythm does not have to match your Facebook one.",
        ],
      },
      {
        heading: "A character counter that agrees with the lexicon",
        body: [
          "Bluesky's post record caps text at 300 characters, but it counts grapheme clusters — not UTF-16 code units, which is what a naive character count returns. A family emoji is one character to Bluesky and up to eleven to JavaScript. Get this wrong and the editor cheerfully lets you write a post the network then rejects.",
          "PostDaily's counter calls the same function the publishing adapter calls, so what the ring says in the composer is what the server validates on the way out. The same applies to the separate UTF-8 byte ceiling in the lexicon, which is the limit long posts with lots of emoji actually hit first.",
        ],
      },
      {
        heading: "Threads are segments, not a wall of text we chop up",
        body: [
          "A thread is composed as explicit segments. You write each post in the chain yourself and attach media to individual segments, rather than typing one long block and hoping an auto-splitter breaks it in a sensible place.",
          "The chain publishes as a real reply chain on Bluesky, in order, with each segment's images attached to the right post.",
        ],
      },
      {
        heading: "When something fails, you find out",
        body: [
          "Every scheduled post runs through a publishing engine built around retries and failure tracking rather than fire-and-forget. A transient error from the network is retried; a genuine failure is recorded against the post with the provider's own error message, and surfaced in the app instead of being swallowed.",
          "If your connection to Bluesky expires, the posts queued behind it are parked rather than burned through one by one at their scheduled times — so reconnecting restores the queue instead of leaving you with a row of failures to clean up.",
        ],
      },
    ],
    caveat: {
      heading: "One thing to know before you schedule video",
      body: "Bluesky's video service requires the account's email address to be confirmed, and it authenticates against your account's real PDS host rather than the bsky.social entryway. If video uploads are rejected while text and image posts publish fine, an unconfirmed email is the usual cause — confirm it in the Bluesky app and reconnect. Text, images and threads are unaffected.",
    },
    faqs: [
      {
        q: "Can I schedule Bluesky threads?",
        a: "Yes. Threads are composed as explicit segments with their own media, and publish as a real reply chain in order.",
      },
      {
        q: "What is the Bluesky character limit?",
        a: "300 characters, counted as grapheme clusters. PostDaily's editor counts them exactly the way Bluesky's lexicon does, so the composer and the server never disagree.",
      },
      {
        q: "Do I need an app password?",
        a: "You connect a Bluesky account with your handle and an app password generated in Bluesky's own settings. PostDaily never asks for your main account password.",
      },
      {
        q: "Does it post to my own PDS?",
        a: "Yes. Posts are written to the personal data server your account actually lives on, whether that is a bsky.network host or one you run yourself.",
      },
      {
        q: "Is there a free plan?",
        a: "Yes — a free plan with no credit card required. Paid plans add channels, team seats and longer history.",
      },
    ],
    helpSlugs: ["connect-and-post-to-bluesky", "when-a-post-fails"],
    tools: [
      {
        to: "/tools/bluesky-best-time",
        label: "Find your own best posting times",
        blurb:
          "Reads your public posts and shows which hours actually beat your median — or says plainly when there is not enough data.",
      },
      {
        to: "/tools/bluesky-engagement-rate",
        label: "Measure your Bluesky engagement rate",
        blurb:
          "Your real rate from your own posts, reported as the median so one post that travelled cannot flatter it.",
      },
    ],
  },
  "mastodon-scheduler": {
    platform: "mastodon",
    slug: "mastodon-scheduler",
    links: [
      {
        blog: "how-to-schedule-mastodon-posts",
        label: "How to schedule Mastodon posts",
      },
      {
        blog: "mastodon-character-limit",
        label: "The Mastodon character limit",
      },
      { blog: "what-is-the-fediverse", label: "What is the fediverse?" },
      {
        blog: "mastodon-for-project-accounts",
        label: "Mastodon for a project or community",
      },
      {
        blog: "best-time-to-post-on-mastodon",
        label: "The best time to post on Mastodon",
      },
      {
        compare: "socialbee",
        label: "Which schedulers really publish to Mastodon",
      },
    ],
    to: "/mastodon-scheduler",
    network: "Mastodon",
    metaTitle: "Mastodon Scheduler for Any Server: Posts & Threads — PostDaily",
    metaDescription:
      "Connect any Mastodon instance over OAuth — no API keys — then schedule posts and threads, with character counting that matches Mastodon's own rules.",
    h1: "Mastodon scheduler for posts and threads, on any server",
    intro:
      "Connect any Mastodon instance over OAuth — no developer application, no API keys — then queue posts and threads and let them publish on a schedule you set.",
    facts: [
      "Any Mastodon server",
      "OAuth, no API keys",
      "500 characters, weighted",
      "Threads and media",
    ],
    sections: [
      {
        heading: "Any server, without registering an application",
        body: [
          "Mastodon is thousands of independently run servers rather than one company, which is exactly why most scheduling tools support it badly or not at all: there is no single API key to obtain.",
          "PostDaily connects to whichever instance your account lives on using standard OAuth. You enter your server's address, approve the connection on your own instance, and that is the whole setup. You do not register a developer application, and you never paste an access token into a form.",
        ],
      },
      {
        heading: "Characters counted the way Mastodon counts them",
        body: [
          "Mastodon does not count posts naively. Every link counts as 23 characters regardless of how long the URL actually is, and the domain part of a mention does not count at all — so @someone@a-very-long-instance.example costs you the same as @someone.",
          "PostDaily's counter implements those rules, using the same shared function the publishing adapter uses. A post full of links reads as far shorter here than a plain character count suggests, which is correct and which most schedulers get wrong.",
        ],
      },
      {
        heading: "Mastodon already schedules one post — this is for the rest",
        body: [
          "Worth saying plainly, because most comparison pages skip it: Mastodon can schedule posts on its own. The catch is where. The scheduler lives in the API, and the official web app and official mobile apps do not expose it — so you reach it through a third-party client instead: Tusky, Fedilab or Moshidon on Android, Mona on iOS, Tuba on desktop, Phanpy or FediPlan on the web. For one account and the occasional post, one of those is genuinely all you need and you do not need us.",
          "What it does not do is give you a calendar to plan a month against, a queue that fills itself from times you set once, several accounts across different servers in one place, per-post analytics, or a way to let someone else approve a post before it goes out. That is the gap PostDaily fills. If none of those sound like your problem, use the built-in one.",
        ],
      },
      {
        heading: "Threads, media and a queue you control",
        body: [
          "Threads publish as a real reply chain, with media attached per segment. Posts are scheduled from a weekly time grid you set per channel, so your fediverse rhythm can differ from the rest of your channels.",
          "Publishing runs through the same retry-and-record engine as every other network: transient errors retry, real failures are recorded with the server's own error message rather than a generic one.",
        ],
      },
    ],
    caveat: {
      heading: "Your server's limit, not a guess at 500",
      body: "PostDaily reads your server's own character limit when you connect and checks it again every day, so a server that allows 5,000 characters gets 5,000, counted the way Mastodon counts. If a server doesn't publish its limit, posts are held to 500, the default every server supports. Media is held to Mastodon's default of 4 attachments per post.",
    },
    faqs: [
      {
        q: "Which Mastodon servers are supported?",
        a: "Any server running Mastodon's standard API. You connect over OAuth by entering your instance's address and approving the request on your own server.",
      },
      {
        q: "Do I need to register a developer application?",
        a: "No. PostDaily registers itself with your instance as part of the OAuth flow. You never create an app or copy an access token by hand.",
      },
      {
        q: "What is the character limit?",
        a: "Whatever your server allows. Mastodon's default is 500 characters, but many servers raise it, and PostDaily reads your server's setting when you connect. Links count as 23 characters and the domain part of a mention is free, as on Mastodon itself.",
      },
      {
        q: "Does Mastodon have a built-in scheduler?",
        a: "Sort of. Scheduling is in Mastodon's API, but the official web app and official mobile apps do not surface it, so you need a third-party client — Tusky, Fedilab or Moshidon on Android, Mona on iOS, Tuba on desktop, Phanpy or FediPlan on the web. Any of those handles one post on one account well. None gives you a content calendar, a self-filling queue, several servers in one place, analytics or approvals.",
      },
      {
        q: "Can I schedule threads?",
        a: "Yes. Threads are written as explicit segments with their own media and publish as a connected reply chain.",
      },
      {
        q: "Is there a free plan?",
        a: "Yes — a free plan with no credit card required.",
      },
    ],
    helpSlugs: ["connect-and-post-to-mastodon", "when-a-post-fails"],
    tools: [
      {
        to: "/tools/mastodon-limits",
        label: "Check your server's real character limit",
        blurb:
          "Your server sets its own limits, and many are not 500. Read them straight from its API — no login.",
      },
      {
        to: "/tools/mastodon-best-time",
        label: "Find your own best posting times",
        blurb:
          "Reads your public posts and shows which hours actually beat your median — or says plainly when there is not enough data.",
      },
    ],
  },
  "facebook-scheduler": {
    platform: "facebook",
    slug: "facebook-scheduler",
    links: [
      {
        blog: "how-to-schedule-facebook-posts",
        label: "How to schedule Facebook posts, Reels and Stories",
      },
      {
        blog: "facebook-scheduled-post-not-posting",
        label: "Facebook scheduled post didn't post? Every cause",
      },
      {
        blog: "schedule-facebook-group-posts",
        label: "Can you schedule posts in a Facebook Group?",
      },
      {
        blog: "schedule-facebook-posts-without-business-suite",
        label: "Schedule Facebook posts without Business Suite",
      },
      { blog: "why-scheduled-posts-fail", label: "Why scheduled posts fail" },
      {
        compare: "meta-business-suite",
        label: "When Meta Business Suite is all you need",
      },
    ],
    to: "/facebook-scheduler",
    network: "Facebook",
    metaTitle: "Facebook Post Scheduler: Pages, Reels, Stories — PostDaily",
    metaDescription:
      "Schedule Facebook Page posts, Reels and Stories, with an automatic first comment, a queue, client approvals and the Page's real error if a post fails.",
    h1: "Facebook post scheduler for Pages, Reels and Stories",
    intro:
      "Plan a month of Page content in one sitting, queue photos, video, Reels and Stories, and publish on a schedule — with a first comment attached and analytics when it lands.",
    facts: [
      "Page posts, photos and video",
      "Reels and Stories",
      "Up to 5 first comments",
      "Post analytics",
    ],
    sections: [
      {
        heading: "One composer for posts, Reels and Stories",
        body: [
          "Facebook treats a feed post, a Reel and a Story as three different things with three different sets of rules. PostDaily puts the format choice above the editor, then adapts the composer to it, so you are not guessing which fields apply or discovering a constraint at publish time.",
          "Photos and video work from the same place, with a live preview of how the post will look before it goes out. A link in your text gets Facebook's own link preview.",
        ],
      },
      {
        heading: "What each format takes",
        body: [
          "**Feed posts:** text, a link, up to 10 photos, or one video. Photos and video can't be mixed in one post, which is Facebook's rule. Up to 5 first comments, published straight after the post.",
          "**Reels:** one video, 3 to 90 seconds. You can invite one collaborating Page. No first comment: Facebook Reels take the caption only, so put the link there. [How to schedule Facebook Reels](/help/schedule-facebook-reels).",
          "**Stories:** one photo or video (a video Story runs 3 to 60 seconds). Stories carry no caption, no comments and no link sticker when published through Meta's API. [How to schedule Facebook Stories](/help/schedule-facebook-stories).",
          "**Collaborators:** one Page, on Reels, video Stories and feed posts with a video.",
        ],
      },
      {
        heading: "First comment, scheduled with the post",
        body: [
          "The first comment is where links and context usually belong on a Page post. PostDaily composes it alongside the post and publishes it immediately afterwards, automatically, so you are not setting an alarm to paste a comment sixty seconds after something goes live. [Put your link in the first comment](/help/facebook-first-comment).",
        ],
      },
      {
        heading: "Meta Business Suite or a scheduler?",
        body: [
          "Meta Business Suite is free and schedules Page posts, Reels and Stories, and it is the right tool if Facebook and Instagram are all you post to. A separate scheduler earns its place when you also post to other networks from the same plan, want a queue that fills your posting times for you, need a client to approve posts without a Facebook login, or want to see why a post failed. [Scheduling without Business Suite](/blog/schedule-facebook-posts-without-business-suite) covers the trade-off in detail.",
        ],
      },
      {
        heading: "A calendar and a queue, not a list of reminders",
        body: [
          "Plan visually on a calendar, drag posts to reschedule, and let a weekly time grid fill the gaps. Bulk-import a month of content from a CSV — including image URLs — when you are planning in a spreadsheet anyway.",
          "Approvals are built in: share a no-login review link with a client, collect their decision, and publish what comes back approved.",
        ],
      },
      {
        heading: "Reliable publishing, with the real error when it is not",
        body: [
          "Scheduled posts run through a publishing engine with retries and durable state, so a transient Graph API error does not cost you a post. When something genuinely fails, the Page's own error message is recorded against the post and surfaced in the app, so you can act on it rather than guess. [Why scheduled posts fail](/blog/why-scheduled-posts-fail).",
          "Once a post lands, its reactions, comments, shares and views are pulled back in and shown next to it, with daily history so you can see how it performed rather than only what it scored today.",
        ],
      },
    ],
    caveat: {
      heading: "Pages only, and no Groups — Meta's rules, not ours",
      body: "Facebook's publishing API works with Pages, not personal profiles, so no third-party scheduler can post to your personal timeline. Groups are out too: Meta removed the Groups publishing permission (publish_to_groups) from every API version on 22 April 2024, so no cloud scheduler can post to a Group any more, whatever its pricing page implies. Group admins can still schedule inside the Group itself.",
    },
    faqs: [
      {
        q: "Does Facebook have a schedule post feature?",
        a: "Yes, for Pages. Meta Business Suite schedules posts, Reels and Stories for a Page you have full control of or content permission on, on desktop and in its mobile app. PostDaily does the same from a queue and a calendar, alongside your other networks.",
      },
      {
        q: "Why can't I schedule posts on Facebook anymore?",
        a: "Usually because you are posting as your personal profile rather than a Page, you don't have content permission on the Page, or you are trying to schedule into a Group through a third-party app, which Meta stopped allowing on 22 April 2024. Switch to the Page, or ask a Page admin for access.",
      },
      {
        q: "Where do I see my scheduled posts?",
        a: "In Meta Business Suite, open Content, then the Scheduled tab under Posts and reels (Stories have their own tab). In PostDaily, every scheduled post is on the Calendar and in the Queue, where you can drag it to a new time.",
      },
      {
        q: "Is there a free Facebook post scheduler?",
        a: "Meta Business Suite is free. PostDaily also has a free plan, with no credit card, that covers one channel, which can be your Facebook Page.",
      },
      {
        q: "Can I schedule to my personal Facebook profile?",
        a: "No. Facebook's API only permits publishing to Pages, which applies to every third-party scheduler. You will need a Facebook Page to schedule posts.",
      },
      {
        q: "Can I schedule Facebook Reels and Stories?",
        a: "Yes. Choose Reel or Story above the editor and the composer applies that format's rules: one 3–90 second video for a Reel, one photo or video for a Story.",
      },
      {
        q: "Can I add a first comment automatically?",
        a: "Yes, on feed posts: up to 5 comments, published immediately after the post goes live. Reels and Stories don't take one.",
      },
      {
        q: "What happens if a scheduled post fails?",
        a: "Transient errors are retried automatically. A genuine failure is recorded against the post with Facebook's own error message and surfaced in the app, and you can retry it once the cause is fixed.",
      },
    ],
    helpSlugs: [
      "connect-a-facebook-page",
      "publish-your-first-facebook-post",
      "schedule-facebook-reels",
      "schedule-facebook-stories",
      "facebook-first-comment",
      "facebook-analytics",
    ],
  },
  "instagram-scheduler": {
    platform: "instagram",
    slug: "instagram-scheduler",
    links: [
      {
        limits: "instagram",
        label: "Instagram caption, hashtag and video limits",
      },
      {
        blog: "social-media-content-calendar",
        label: "A content calendar that survives a real week",
      },
      { blog: "why-scheduled-posts-fail", label: "Why scheduled posts fail" },
      { compare: "later", label: "PostDaily vs Later for Instagram" },
    ],
    to: "/instagram-scheduler",
    network: "Instagram",
    metaTitle:
      "Instagram Scheduler: Posts, Reels, Stories & Carousels — PostDaily",
    metaDescription:
      "Schedule Instagram posts, Reels, Stories and photo carousels with first comments, collaborators, people tags and alt text. Business and Creator accounts.",
    h1: "Instagram scheduler for posts, Reels, Stories and carousels",
    intro:
      "Schedule the feed, Reels and Stories from one composer, with the details Instagram's own scheduler leaves out: Stories, collaborators and a first comment that goes out with the post.",
    facts: [
      "Posts, Reels, Stories, carousels",
      "Up to 5 first comments",
      "Collaborators & people tags",
      "Business & Creator accounts",
    ],
    sections: [
      {
        heading: "What Instagram's own scheduler covers, and what it doesn't",
        body: [
          "The Instagram app can schedule posts, Reels and carousels on a public account, up to 25 a day and 30 days ahead. It can't schedule Stories, and Instagram's help centre says collaborative posts and product tagging don't work with scheduled content. PostDaily schedules all four formats up to 12 months ahead, and invites collaborators on scheduled posts.",
        ],
      },
      {
        heading: "The options that matter on Instagram",
        body: [
          "**People tags:** up to 20, placed where you tap on the image. **Collaborators:** up to 3 accounts invited to co-author the post, so it appears on their profile too. **Alt text** on feed and carousel images. On a Reel, pick the cover frame or upload a cover, choose whether it also shows in the feed, and rename the audio. The AI-generated label is one switch.",
          "A first comment, up to 5, publishes straight after the post on feed posts, carousels and Reels. Stories take no comments, so the option disappears when Story is selected.",
        ],
      },
      {
        heading: "Carousels are photo carousels",
        body: [
          `A carousel holds up to 10 photos. Adding a video switches the post to a Reel, because PostDaily publishes Instagram video as Reels. How many items fit in one post also depends on your plan: ${MEDIA_PER_POST}, so a full 10-photo carousel needs Agency.`,
        ],
      },
      {
        heading: "Stories, one frame at a time",
        body: [
          "A Story is one photo or video, and a video Story runs 3 to 60 seconds. Attach several images and they publish as consecutive frames. Each frame counts as a post against Meta's daily publishing cap, so a long Story can hit the cap part-way through; the post records which frames went out.",
        ],
      },
      {
        heading: "Instagram Login or a Facebook Page connection",
        body: [
          "Connect with Instagram Login for the fewest steps, or through the Facebook Page the account is linked to. Choose the Page route if you want collaborators: they only work on that connection, and on Instagram Login they are skipped with a notice rather than failing the post. [How to connect Instagram](/help/connect-instagram).",
        ],
      },
      {
        heading: "When Instagram says no",
        body: [
          "Meta caps how many posts an account can publish through the API in a rolling 24 hours. The number isn't fixed across accounts, so PostDaily reads your account's live cap and shows it in the composer. If a publish hits the cap, it is retried once the window moves on rather than dropped, and any other refusal is recorded against the post in Instagram's own words.",
        ],
      },
    ],
    caveat: {
      heading:
        "Business and Creator accounts only, and a few things Meta's API can't do",
      body: "Instagram only lets scheduling tools publish to professional accounts, so a personal account has to switch to Business or Creator first (it's free, in the app). Product tags and location tags aren't supported, and neither licensed music nor interactive Story stickers can be added through Meta's API. Instagram doesn't allow text-only posts.",
    },
    faqs: [
      {
        q: "Can I schedule Instagram Stories?",
        a: "Yes. Choose Story, attach one photo or video (or several images, which publish as consecutive frames), and pick a time. The Instagram app itself can't schedule Stories; Meta Business Suite and tools like PostDaily can.",
      },
      {
        q: "Can I add a first comment automatically?",
        a: "Yes, up to 5 on feed posts, carousels and Reels. They publish right after the post. Stories don't take comments.",
      },
      {
        q: "Can I schedule to a personal Instagram account?",
        a: "No. Instagram only allows scheduled publishing to Business and Creator accounts. Switching is free in the Instagram app and keeps your posts and followers.",
      },
      {
        q: "Do scheduled posts get less reach?",
        a: "Meta hasn't said that scheduled posts are ranked differently, and Instagram's own app schedules them. What changes reach is the post itself and when your audience is around.",
      },
      {
        q: "Is there a free plan?",
        a: `Yes, ${FREE_PLAN}. That channel can be your Instagram account.`,
      },
    ],
    helpSlugs: [
      "connect-instagram",
      "publish-to-instagram",
      "schedule-instagram-stories",
      "character-limits-by-network",
    ],
    tools: [
      {
        to: "/tools/video-checker",
        label: "Video format checker",
        blurb:
          "Drop a Reel before you upload it and see whether Instagram will take the codec, size and length. The file never leaves your browser.",
      },
    ],
  },
  "tiktok-scheduler": {
    platform: "tiktok",
    slug: "tiktok-scheduler",
    links: [
      { limits: "tiktok", label: "TikTok video, caption and photo limits" },
      { blog: "why-scheduled-posts-fail", label: "Why scheduled posts fail" },
      {
        blog: "social-media-content-calendar",
        label: "Plan a month of content",
      },
    ],
    to: "/tiktok-scheduler",
    network: "TikTok",
    metaTitle: "TikTok Scheduler: Videos & Photo Posts — PostDaily",
    metaDescription:
      "Schedule TikTok videos and photo carousels, publish automatically or get a reminder to finish in the app, with privacy, Duet, Stitch and disclosure controls.",
    h1: "TikTok scheduler for videos and photo carousels",
    intro:
      "Queue TikTok videos and photo posts next to everything else you publish, and choose per post whether it goes out on its own or lands in your TikTok inbox to finish.",
    facts: [
      "Post automatically or get a reminder",
      "Photo carousels",
      "Privacy & interaction controls",
      "Captions up to 2,200",
    ],
    sections: [
      {
        heading: "Post automatically, or get a reminder",
        body: [
          "**Post automatically** publishes the video or photo post to your profile at the scheduled time. **Get a reminder** sends it to your TikTok inbox as a draft instead: you open TikTok, pick the audience and publish within 24 hours. Only the caption carries over, so a reminder is the route when you want to add a sound from TikTok's library, which apps can't attach.",
          "TikTok Studio has a scheduler of its own. PostDaily's reason to exist is the rest of your week: the same vertical video queued for TikTok, Reels and Shorts, each with its own caption, up to 12 months ahead.",
        ],
      },
      {
        heading: "Every TikTok control, set before it goes out",
        body: [
          "**Privacy level** is required and has no default, and it is checked again against your account's current options when the post publishes. **Comments, Duet and Stitch** stay off until you turn them on. **Commercial content** can be marked as promoting your own brand or as branded content, and branded content can't be set to \"Only me\", which is TikTok's rule. There is an AI-generated label, a cover frame for videos, and for photo posts a cover photo and an auto-add music switch.",
        ],
      },
      {
        heading: "Photo carousels",
        body: [
          `TikTok takes up to 35 photos in a photo post; PostDaily's plans cap the items in one post at ${MEDIA_PER_POST}. The first 90 characters of the caption become the post's title. Photos can't be mixed with video in one post.`,
        ],
      },
      {
        heading: "When TikTok refuses a post",
        body: [
          "TikTok's spam and posting-frequency limits come back as rate limits, so PostDaily waits and retries rather than failing the post outright. Anything else is recorded against the post in TikTok's words. A post's public link appears once TikTok's moderation releases it, which can take a few hours.",
        ],
      },
    ],
    caveat: {
      heading: "What TikTok's API can't do, and what we don't check",
      body: `Sounds from TikTok's music library can't be added through the API, so use Get a reminder when a video needs one. PostDaily doesn't check a video's length against your account's maximum before publishing, so TikTok may refuse a long upload at publish time. Uploads are capped per file by plan: ${UPLOAD_CAP}. Photo posts return no metrics; videos report views, likes, comments and shares.`,
    },
    faqs: [
      {
        q: "Why can't I schedule on TikTok?",
        a: "TikTok's own scheduler lives in TikTok Studio, and whether you see it depends on your device and account. A tool like PostDaily schedules from any device, photo posts included.",
      },
      {
        q: "Can I schedule TikTok photo posts?",
        a: `Yes. Attach the photos and schedule as usual. TikTok allows up to 35; your plan allows ${MEDIA_PER_POST} per post.`,
      },
      {
        q: "Can I add music?",
        a: "Not from TikTok's library: apps can't attach those sounds. Put the sound in the video file, turn on auto-add music for a photo post, or use Get a reminder and add the sound in TikTok before publishing.",
      },
      {
        q: "Do scheduled TikToks get fewer views?",
        a: "TikTok hasn't said that posts published through its API are ranked differently. Views depend on the video and the first seconds of it, not on how it was published.",
      },
      {
        q: "Is there a free plan?",
        a: `Yes, ${FREE_PLAN}.`,
      },
    ],
    helpSlugs: [
      "connect-and-post-to-tiktok",
      "when-a-post-fails",
      "character-limits-by-network",
      "tailor-a-post-per-channel",
    ],
    tools: [
      {
        to: "/tools/video-checker",
        label: "Video format checker",
        blurb:
          "Check a video's codec, size and length against TikTok's rules before you upload it. The file never leaves your browser.",
      },
    ],
  },
  "pinterest-scheduler": {
    platform: "pinterest",
    slug: "pinterest-scheduler",
    links: [
      { limits: "pinterest", label: "Pinterest Pin size and limits" },
      {
        blog: "social-media-content-calendar",
        label: "A content calendar that survives a real week",
      },
    ],
    to: "/pinterest-scheduler",
    network: "Pinterest",
    metaTitle: "Pinterest Scheduler: Pins, Carousels & Video — PostDaily",
    metaDescription:
      "Schedule image Pins, carousels and video Pins to any board or section, bulk upload a CSV of Pins, and see each Pin's impressions and saves.",
    h1: "Pinterest scheduler for Pins, carousels and video Pins",
    intro:
      "Pick a board, write the Pin once, and queue a month of them — without Pinterest's own ten-Pin ceiling on what can be scheduled.",
    facts: [
      "Image, carousel & video Pins",
      "Board & section picker",
      "Bulk CSV to a board",
      "Pin impressions & saves",
    ],
    sections: [
      {
        heading: "Past Pinterest's ten-Pin limit",
        body: [
          "Pinterest's own scheduler holds up to 10 Pins at a time, created one at a time, up to 30 days ahead (Pinterest Business help, checked 27 September 2026). PostDaily's queue has no count limit beyond your plan and reaches 12 months ahead, so a season of Pins can be planned in one sitting.",
        ],
      },
      {
        heading: "Board, section, title and link",
        body: [
          "A board is required and is picked from your account's own list, including secret boards; a section is optional. Each Pin also takes a title (up to 100 characters), a destination link, alt text (up to 500) and the description, which is the post text (up to 800).",
        ],
      },
      {
        heading: "Carousels and video Pins",
        body: [
          `A carousel Pin takes 2 to 5 images. A video Pin needs a cover, and PostDaily uses the first image in the post for it, so attach one alongside the video. Your plan caps media per post (${MEDIA_PER_POST}) and file size per upload (${UPLOAD_CAP}).`,
        ],
      },
      {
        heading: "Bulk upload a CSV of Pins to a board",
        body: [
          "Planning in a spreadsheet already? Upload a CSV and each row becomes a scheduled Pin. The upload asks for one board per file, so Pins for several boards take one file each, and rows without an image URL are flagged before anything is imported. A board section can't be set in bulk. [Bulk upload from a CSV](/help/bulk-upload-csv).",
        ],
      },
      {
        heading: "Pin analytics, read live",
        body: [
          "Open a Pin and PostDaily reads its impressions and saves over the last 30 days straight from Pinterest. They aren't stored, because Pinterest's developer terms only allow apps to keep campaign analytics, so there is no long-term chart. Analytics need a Pinterest business account.",
        ],
      },
    ],
    caveat: {
      heading: "A scheduler, not a Pinterest growth suite",
      body: "PostDaily publishes Pins reliably next to your other networks. It doesn't do Pinterest keyword research, group boards or automatic re-pinning; Pinterest-only tools such as Tailwind are built for that. Carousels are 2 to 5 images, video Pins need a cover image, and text-only Pins don't exist.",
    },
    faqs: [
      {
        q: "How far ahead can I schedule Pins?",
        a: "Up to 12 months in PostDaily, with no limit on how many are queued beyond your plan. Pinterest's own scheduler holds 10 Pins, up to 30 days ahead.",
      },
      {
        q: "Can I schedule video Pins?",
        a: "Yes. Attach the video and one image; the image becomes the video Pin's cover.",
      },
      {
        q: "Can I schedule to a board section?",
        a: "Yes, when you schedule a Pin in the composer: pick the board, then the section. Bulk CSV uploads go to a board without a section.",
      },
      {
        q: "Does it work with a personal Pinterest account?",
        a: "Publishing works with any account. Pin analytics need a Pinterest business account, which is free to switch to.",
      },
      {
        q: "Can I bulk upload Pins?",
        a: "Yes: one CSV per board, and every row needs an image URL. The upload flags rows without one before importing.",
      },
      {
        q: "Is there a free plan?",
        a: `Yes, ${FREE_PLAN}.`,
      },
    ],
    helpSlugs: [
      "connect-and-post-to-pinterest",
      "bulk-upload-csv",
      "when-a-post-fails",
      "plan-with-the-calendar",
    ],
  },
  "youtube-scheduler": {
    platform: "youtube",
    slug: "youtube-scheduler",
    links: [
      {
        limits: "youtube-shorts",
        label: "YouTube Shorts length and size limits",
      },
      { blog: "why-scheduled-posts-fail", label: "Why scheduled posts fail" },
    ],
    to: "/youtube-scheduler",
    network: "YouTube",
    metaTitle: "YouTube Scheduler for Shorts & Videos — PostDaily",
    metaDescription:
      "Schedule YouTube Shorts and videos with title, tags, category, made-for-kids, thumbnail and a first comment, alongside TikTok and Reels.",
    h1: "Schedule YouTube Shorts and videos alongside everything else",
    intro:
      "Upload a Short or a video with every YouTube field set, and send the same vertical video to TikTok and Reels in the same post.",
    facts: [
      "Shorts & videos",
      "Title, tags & category",
      "Up to 5 first comments",
      "Made-for-kids & disclosure",
    ],
    sections: [
      {
        heading: "If YouTube is all you post to, use YouTube Studio",
        body: [
          "YouTube Studio schedules uploads for free and does everything a YouTube-only channel needs. PostDaily is worth it when the same vertical video also goes to TikTok and Instagram Reels: write one post, give each network its own caption, and schedule them together.",
        ],
      },
      {
        heading: "Every YouTube field, set once",
        body: [
          "**Title** up to 100 characters, defaulting to the post's first line. **Description** up to 5,000 bytes, which is fewer characters in emoji or non-Latin scripts. **Tags** up to 60, 500 characters together, and a category. **Visibility**, whether to notify subscribers, licence, embedding and whether the view count shows. Title, description and spoken languages, the AI disclosure, and a custom thumbnail on channels allowed one.",
          "**Made for kids** is required and has no default: YouTube needs the answer, so PostDaily asks rather than guessing. Playlists aren't offered in the composer yet.",
        ],
      },
      {
        heading: "Shorts",
        body: [
          "Choose Short and PostDaily adds #Shorts so YouTube files it correctly; it pre-selects Short when the video is vertical and three minutes or less. It warns if a Short runs over three minutes, because YouTube decides from the file itself.",
        ],
      },
      {
        heading: "First comments on YouTube",
        body: [
          "Up to 5 comments publish under the video as soon as it's live, which is where most channels pin a link. They are skipped on private videos, because YouTube doesn't allow comments there.",
        ],
      },
      {
        heading: "Upload size",
        body: [
          `Uploads are capped per file by plan: ${UPLOAD_CAP}. That is far below YouTube's own limit, so long videos are better uploaded in YouTube Studio; Shorts and short clips fit comfortably.`,
        ],
      },
    ],
    caveat: {
      heading: "Built for Shorts and short videos, not long-form uploads",
      body: `The largest upload any plan allows is ${LARGEST_UPLOAD_MB} MB per file, which rules out most long videos. Whether an upload counts as a Short is YouTube's call, based on its length and shape. Channels that can't use custom thumbnails will have the thumbnail skipped. Metrics include views, likes, comments and watch time, but not impressions.`,
    },
    faqs: [
      {
        q: "Can I schedule YouTube Shorts?",
        a: "Yes. Attach a vertical video of three minutes or less, choose Short, and pick a time. PostDaily adds #Shorts for you.",
      },
      {
        q: "Will my upload show as a Short?",
        a: "YouTube decides from the video: vertical or square, and three minutes or less. PostDaily pre-selects Short when the file fits and warns when a Short is too long.",
      },
      {
        q: "Can I set made-for-kids, tags and a thumbnail?",
        a: "Yes, all three, plus category, visibility, licence, languages and the AI disclosure. Made-for-kids is required.",
      },
      {
        q: "Can I post the same video to TikTok and Reels?",
        a: "Yes. Select YouTube, TikTok and Instagram in one post and give each its own caption; each network gets its own upload at the scheduled time.",
      },
    ],
    helpSlugs: ["connect-youtube", "publish-to-youtube"],
    tools: [
      {
        to: "/tools/video-checker",
        label: "Video format checker",
        blurb:
          "See whether a video will upload to YouTube, TikTok and Reels before you schedule it. The file stays in your browser.",
      },
    ],
  },
  "threads-scheduler": {
    platform: "threads",
    slug: "threads-scheduler",
    links: [
      {
        blog: "bluesky-vs-mastodon-vs-threads",
        label: "Bluesky vs Mastodon vs Threads",
      },
      {
        blog: "cross-post-bluesky-mastodon",
        label: "Cross-posting between networks",
      },
    ],
    to: "/threads-scheduler",
    network: "Threads",
    metaTitle: "Threads Scheduler: Posts & Chains — PostDaily",
    metaDescription:
      "Schedule posts and multi-post chains to Threads, Meta's text app, with per-post media, reply controls and topic tags, and send the same chain to Bluesky and X.",
    h1: "Schedule Threads posts and multi-post chains",
    intro:
      "Threads is Meta's text app, the one linked to your Instagram account. Schedule single posts or whole chains to it, and send the same chain to Bluesky, Mastodon and X.",
    facts: [
      "Text, image, video & carousels",
      "Multi-post chains",
      "Reply controls & topic tags",
      "Cross-post to Bluesky, Mastodon & X",
    ],
    sections: [
      {
        heading: "Chains, written as separate posts",
        body: [
          "Write a chain as explicit posts, each with its own photos or video, up to 25 in a row. PostDaily publishes the first, then each next one as a reply to the one before, which is how a chain works on Threads, and a week of chains can sit in the queue at once.",
        ],
      },
      {
        heading: "Who can reply, topic tags and links",
        body: [
          "Choose who can reply: everyone, profiles you follow, your followers, mentioned profiles only, or only you. Add a topic tag (up to 50 characters, without full stops or ampersands) and, on a text-only post, a link attachment. These apply to the first post of a chain.",
        ],
      },
      {
        heading: "One chain, four networks, counted four ways",
        body: [
          "Threads allows 500 characters a post. The same chain can go to Bluesky (300), Mastodon (500, with links counted as 23) and X (280, weighted), and the composer counts each the way that network does, so nothing is cut off on the way out. [Split a draft into a thread](/tools/thread-splitter).",
        ],
      },
      {
        heading: "Media on Threads",
        body: [
          `A carousel takes up to 20 photos and videos mixed, and videos run up to 5 minutes. Your plan caps items per post at ${MEDIA_PER_POST}, which is lower than Threads' 20.`,
        ],
      },
    ],
    caveat: {
      heading: "No first comment on Threads — use the chain",
      body: "On Threads the follow-up belongs in the chain itself, so the composer offers Add to thread instead of a first-comment box. Chains need the Threads reply permission, which PostDaily asks for when you connect. Meta also caps how many posts an app can publish per profile each day, which a very busy account can reach.",
    },
    faqs: [
      {
        q: "Can you schedule posts on Threads?",
        a: "Yes. PostDaily schedules single posts, photo and video carousels and whole chains, up to 12 months ahead.",
      },
      {
        q: "Can I schedule a Threads chain?",
        a: "Yes. Write each post of the chain separately, with its own media, and PostDaily publishes them as a reply chain at the scheduled time.",
      },
      {
        q: "Can I post the same thing to Bluesky and Threads?",
        a: "Yes. Select both in one post. Each network's character limit is counted its own way, and you can give either one its own text.",
      },
      {
        q: "What is the Threads character limit?",
        a: "500 characters per post. Longer ideas go in a chain of posts.",
      },
    ],
    helpSlugs: ["connect-and-post-to-threads", "character-limits-by-network"],
    tools: [
      {
        to: "/tools/thread-splitter",
        label: "Thread splitter",
        blurb:
          "Paste a long draft and split it into numbered posts that fit Threads, Bluesky, Mastodon or X.",
      },
    ],
  },
  "twitter-scheduler": {
    platform: "x",
    slug: "twitter-scheduler",
    links: [
      { blog: "bluesky-threads-guide", label: "Writing a thread people read" },
      { blog: "why-scheduled-posts-fail", label: "Why scheduled posts fail" },
    ],
    to: "/twitter-scheduler",
    network: "X (Twitter)",
    metaTitle: "X (Twitter) Scheduler: Posts, Threads & Polls — PostDaily",
    metaDescription:
      "Schedule posts, threads and polls to X (Twitter) with reply settings and character counting that matches X's. On paid plans, with a monthly X budget.",
    h1: "X (Twitter) scheduler for posts, threads and polls",
    intro:
      "Schedule posts, threads and polls to X, and send the same thread to Threads, Bluesky and Mastodon, each counted the way that network counts.",
    facts: [
      "Posts, threads & polls",
      "Reply settings",
      "Weighted character counting",
      "Paid plans",
    ],
    sections: [
      {
        heading: "Counted the way X counts",
        body: [
          "X allows 280 characters, weighted: every link counts as 23 however long it is, and emoji and Chinese, Japanese or Korean characters count as 2. PostDaily's counter uses the same rules, so a post it accepts is a post X accepts. There are no long posts through the API.",
        ],
      },
      {
        heading: "Threads, polls and reply settings",
        body: [
          "Write a thread as separate posts, each with its own images or video, up to 25. Add a poll with 2 to 4 choices of up to 25 characters, open from 5 minutes to 7 days (a poll can't carry media). Choose who can reply: everyone, accounts you follow, mentioned users, subscribers or verified accounts. Polls and reply settings apply to the first post.",
        ],
      },
      {
        heading: "The monthly X budget",
        body: [
          `X charges for every post made through its API, so each paid plan includes a monthly X budget: ${X_PLAIN}. A post with a link costs X about 13 times as much, so a month of link posts covers ${X_LINKS} (a link typed without https:// still counts). Each post in a thread counts on its own, and reading a post's metrics uses a little of the budget too. Your billing page shows what is left.`,
        ],
      },
      {
        heading: "When X says no",
        body: [
          "When the month's budget runs out, X posts fail with a message saying so and aren't retried automatically, so nothing spends past it; retry them once the budget resets. X rejects a post whose text duplicates a recent one, and PostDaily records that in X's words rather than retrying it.",
        ],
      },
    ],
    caveat: {
      heading: "X is on paid plans only",
      body: "X charges per post through its API, so it isn't on the Free plan, and each paid plan's X budget is finite. If X is the only network you post to, X's own tools may be all you need. Uploads accept JPEG, PNG, WebP and video, not GIF.",
    },
    faqs: [
      {
        q: "Is X scheduling free?",
        a: "Not in PostDaily: X charges per post through its API, so X is on the paid plans, each with a monthly X budget.",
      },
      {
        q: "How many X posts can I schedule a month?",
        a: `With plain posts, ${X_PLAIN}. Posts with links cost about 13 times as much: ${X_LINKS}. Each post in a thread counts separately.`,
      },
      {
        q: "Can I schedule threads and polls?",
        a: "Yes. Threads of up to 25 posts, each with its own media, and polls of 2 to 4 choices on posts without media.",
      },
      {
        q: "How are characters counted?",
        a: "The way X counts them: 280 per post, links count as 23, and emoji and CJK characters count as 2.",
      },
    ],
    helpSlugs: [
      "connect-and-post-to-x",
      "character-limits-by-network",
      "manage-your-plan",
      "when-a-post-fails",
    ],
    tools: [
      {
        to: "/tools/thread-splitter",
        label: "Thread splitter",
        blurb:
          "Split a long draft into a numbered X thread, counted with X's own weighting for links and emoji.",
      },
    ],
  },
  "linkedin-scheduler": {
    platform: "linkedin",
    slug: "linkedin-scheduler",
    to: "/linkedin-scheduler",
    network: "LinkedIn",
    metaTitle: "LinkedIn Scheduler for Profiles & Company Pages — PostDaily",
    metaDescription:
      "Schedule LinkedIn posts to your profile and the Company Pages you run: text, images, video, PDF carousels and article links, with first comments.",
    h1: "LinkedIn scheduler for your profile and Company Pages",
    intro:
      "Write the post once, choose your profile or a Page you run, and queue it with a first comment, the audience it reaches and whether it can be reshared.",
    facts: [
      "Profiles & Company Pages",
      "PDF carousels & video",
      `First comments (up to ${MAX_FIRST_COMMENTS})`,
      "Page post analytics",
    ],
    sections: [
      {
        heading: "Your profile and the Pages you run",
        body: [
          "Connect your LinkedIn account and PostDaily adds your personal profile as a channel, plus each Company Page you administer, with its own queue and posting times. LinkedIn only lets a Page admin post as the Page, so a Page you can't see in your own admin list won't appear here either.",
        ],
      },
      {
        heading: "Text, images, video, PDF carousels and article links",
        body: [
          `A post takes up to 3,000 characters. Attach images, one MP4 video, or one PDF, which LinkedIn shows as a swipeable carousel and which needs a title (up to 100 characters) and must be under 25 MB. A link becomes an article card with its own title and description. How many items one post holds depends on your plan: ${MEDIA_PER_POST}; file size per upload is ${UPLOAD_CAP}.`,
        ],
      },
      {
        heading: "First comments, audience and resharing",
        body: [
          `Add up to ${MAX_FIRST_COMMENTS} first comments (1,250 characters each) to go up under the post as soon as it is live, which is where a link usually goes so the post itself keeps its reach. Choose who sees a profile post (anyone, your connections or logged-in members) and switch off resharing when a post should stay where it is. Page posts are always public.`,
        ],
      },
      {
        heading: "Page analytics and comments",
        body: [
          "For Company Page posts, PostDaily reads impressions, unique views, clicks and reactions, and shows the post's comments live in Post details. LinkedIn doesn't give scheduling apps the analytics of personal-profile posts, so those stay in LinkedIn.",
        ],
      },
    ],
    caveat: {
      heading: "Still in LinkedIn's review",
      body: "LinkedIn is still reviewing PostDaily's access. Publishing works today, but until that review is done every PostDaily customer shares one daily allowance of LinkedIn requests. A post LinkedIn turns away for that reason is retried automatically; on a very busy day it can still fail, and then you see why and can retry it. If you post to LinkedIn at volume, weigh that until the review is done.",
    },
    faqs: [
      {
        q: "Can I schedule posts to a LinkedIn Company Page?",
        a: "Yes, to any Page you are an admin of. Each Page becomes its own channel with its own queue.",
      },
      {
        q: "Can I schedule a PDF carousel on LinkedIn?",
        a: "Yes. Attach one PDF under 25 MB and give it a title; LinkedIn shows it as a swipeable document.",
      },
      {
        q: "Can I add a first comment to a scheduled LinkedIn post?",
        a: `Yes, up to ${MAX_FIRST_COMMENTS}, each up to 1,250 characters. They go up right after the post.`,
      },
      {
        q: "How far ahead can I schedule LinkedIn posts?",
        a: "Up to 12 months ahead, with no limit on how many are queued beyond your plan.",
      },
      {
        q: "Will I see analytics for my LinkedIn posts?",
        a: "For Company Page posts, yes: impressions, unique views, clicks and reactions. LinkedIn doesn't share personal-profile post analytics with scheduling apps.",
      },
      {
        q: "Is there a free plan?",
        a: `Yes, ${FREE_PLAN}. That channel can be your LinkedIn profile or a Page.`,
      },
    ],
    helpSlugs: [
      "plan-with-the-calendar",
      "when-a-post-fails",
      "channel-stopped-working",
    ],
  },
} satisfies Record<string, NetworkLanding>;

export type NetworkLandingSlug = keyof typeof NETWORK_LANDINGS;

/** Every landing, for the footer links and the prerender/sitemap list. */
export const NETWORK_LANDING_LIST: NetworkLanding[] =
  Object.values(NETWORK_LANDINGS);

const BY_PLATFORM = new Map(
  NETWORK_LANDING_LIST.map((landing) => [landing.platform, landing]),
);

/** The landing page for a platform, when one exists (only live networks have one). */
export function networkLandingFor(
  platform: PlatformId,
): NetworkLanding | undefined {
  return BY_PLATFORM.get(platform);
}
